import React, { createContext, useState, useEffect, useRef } from 'react';
import axios from '../api/axiosConfig';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState(new Set()); // Store favorited recipe IDs
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  // Fetch the logged-in user and their favorited recipes
  useEffect(() => {
    const fetchUserAndFavorites = async () => {
      try {
        const userResponse = await axios.get("/auth/user", { withCredentials: true });
        setUser(userResponse.data);

        if (userResponse.data) {
          await fetchFavorites();
        } else {
          setFavorites(new Set()); // Reset favorites on logout
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        setUser(null);
        setFavorites(new Set()); // Reset favorites on error
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      fetchUserAndFavorites();
      hasFetched.current = true;
    }
  }, []);

  // Fetch user's favorite recipes
  const fetchFavorites = async () => {
    try {
      const response = await axios.get("/api/user/recipes/saved", { withCredentials: true });

      setFavorites(new Map(response.data.map((recipe) => [recipe.id, recipe])));
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };


  const addFavorite = async (recipeId) => {
    try {
      await axios.post("/api/user/recipes/save", { recipeId }, { withCredentials: true });

      await fetchFavorites();
    } catch (error) {
      console.error("Error saving recipe:", error);
    }
  };

  const removeFavorite = async (recipeId) => {
    try {
      await axios.post("/api/user/recipes/remove", { recipeId }, { withCredentials: true });

      await fetchFavorites();
    } catch (error) {
      console.error("Error removing recipe:", error);
    }
  };


  const login = async () => {
    const popup = window.open(
      `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8080"}/auth/google`,
      "Login with Google",
      "width=500,height=600"
    );

    const interval = setInterval(async () => {
      try {
        if (popup.closed) {
          clearInterval(interval);
          const userResponse = await axios.get("/auth/user", { withCredentials: true });
          setUser(userResponse.data);
          if (userResponse.data) {
            await fetchFavorites(); // Refetch favorites after login
          }
        }
      } catch (error) {
        console.error("Error checking login:", error);
      }
    }, 500);
  };

  const logout = async () => {
    await axios.get('/auth/logout', { withCredentials: true });
    setUser(null);
    setFavorites(new Set()); // Reset favorites on logout
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, favorites, addFavorite, removeFavorite }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
